// Portal público «Pit Stop» (sin login) — /pitstop/:token.
// El gamer abre el enlace (QR del ticket o WhatsApp) y sigue su reparación en
// vivo: línea de tiempo, fotos, aprobación del presupuesto y pago del anticipo
// por enlace/QR. Sin ventanas modales: todo integrado en la página.
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  AlertTriangle, BadgeCheck, Banknote, CalendarClock, Camera, CheckCircle2, ChevronDown, ChevronRight,
  CreditCard, Download, ExternalLink, Gamepad2, KeyRound, Loader2, QrCode, Share2, ShieldCheck, Sparkles, ThermometerSun, Wrench, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { tallerRpc, ESTADOS, fmtFecha, fmtFechaHora, urlPasaporte, useDialogoEnLinea, type EstadoOrden } from './shared';

// ── Tipos de la respuesta del portal (taller.portal_orden) ─────────────────
interface PortalLinea {
  id: string; tipo: 'diagnostico' | 'mano_obra' | 'repuesto'; descripcion: string; cantidad: number;
  precio: number; iva_pct: number; total: number; garantia_dias: number | null;
}
interface PortalPresupuesto { version: number; adicional: boolean; lineas: PortalLinea[] }
interface PortalEvento { estado: string | null; nota: string | null; fecha: string }
interface PortalFoto { id: string; mime: string; etapa: string; descripcion: string | null; fecha: string }
interface PortalCobro { id: string; url: string | null; qr: string | null; monto: number; estado: string; medio: string }
interface PortalOrden {
  taller: { nombre: string; terminos: string | null; garantia_dias: number } | null;
  codigo: string; estado: EstadoOrden; falla: string; fecha: string; fecha_prometida: string | null;
  garantia_hasta: string | null; trabajo_realizado: string | null; cliente: string;
  equipo: { tipo: string; marca: string | null; modelo: string | null };
  tecnico: string | null;
  eventos: PortalEvento[];
  fotos: PortalFoto[];
  presupuesto: PortalPresupuesto | null;
  aprobado: { subtotal: number; iva: number; total: number };
  anticipo_pagado: number;
  anticipo_sugerido: number | null;
  cobro_pendiente: PortalCobro | null;
  pago_en_linea: boolean;
  /** 954: pasaporte del equipo + traspaso al vender. */
  pasaporte: string | null;
  puede_traspasar: boolean;
  garantia_cubre: boolean;
  /** 955: certificado de bench (null hasta que se certifique la orden). */
  certificado: { codigo: string; firmado: boolean; pdf: boolean } | null;
}
interface RespuestaPresupuesto { ok: boolean; estado: EstadoOrden; aprobadas: number; total: { subtotal: number; iva: number; total: number } }
interface RespuestaPago { ok: boolean; cobro: { id: string; url: string | null; qr: string | null; monto: number } }
interface RespuestaTraspaso { ok: boolean; codigo: string; expira: string }
interface RespuestaBenchPdf { pdf: string | null; codigo: string }

const money = (n: number | null | undefined) => `$${Number(n ?? 0).toFixed(2)}`;
/** URL pública de verificación del certificado de bench (destino del QR del PDF). */
const urlVerificarBench = (codigo: string) => `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/verificar-bench/${codigo}`;
function descargarPdfBase64(b64: string, filename: string) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Barra de niveles (progreso tipo «gamer») ────────────────────────────────
const PASOS: string[] = ['Recibido', 'Diagnóstico', 'Presupuesto', 'Reparación', 'Pruebas', 'Listo', 'Entregado'];
// ESTADOS[*].paso va de 1 a 8 (aprobado/rechazado comparten el 4 con la cola de reparación);
// se colapsa a los 7 niveles visibles del Pit Stop.
const PASO_A_NIVEL: Record<number, number> = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 3, 6: 4, 7: 5, 8: 6 };

function BarraNiveles({ estado }: { estado: EstadoOrden }) {
  const nivelActual = PASO_A_NIVEL[ESTADOS[estado].paso] ?? 0;
  const negativo = estado === 'rechazado' || estado === 'devuelto';
  return (
    <div className="flex items-center justify-between gap-0.5">
      {PASOS.map((p, i) => {
        const hecho = i < nivelActual;
        const actual = i === nivelActual;
        return (
          <div key={p} className="flex flex-1 flex-col items-center gap-1.5">
            <div className="flex w-full items-center">
              {i > 0 && <div className={cn('h-0.5 flex-1', i <= nivelActual ? (negativo && actual === false && i === nivelActual ? 'bg-rose-500' : 'bg-emerald-400') : 'bg-white/15')} />}
              <div
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold',
                  hecho && 'border-emerald-400 bg-emerald-400 text-slate-950',
                  actual && !negativo && 'border-fuchsia-400 bg-fuchsia-500 text-white shadow-[0_0_16px_rgba(217,70,239,0.8)] animate-pulse',
                  actual && negativo && 'border-rose-400 bg-rose-500 text-white shadow-[0_0_16px_rgba(244,63,94,0.7)]',
                  !hecho && !actual && 'border-white/20 bg-white/5 text-white/40'
                )}
              >
                {hecho ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              {i < PASOS.length - 1 && <div className={cn('h-0.5 flex-1', i < nivelActual ? 'bg-emerald-400' : 'bg-white/15')} />}
            </div>
            <span className={cn('text-center text-[9px] leading-tight sm:text-[10px]', actual ? 'font-semibold text-white' : 'text-white/45')}>{p}</span>
          </div>
        );
      })}
    </div>
  );
}

function GaleriaFotos({ token, fotos }: { token: string; fotos: PortalFoto[] }) {
  const [ampliada, setAmpliada] = useState<string | null>(null);
  const resultados = useQueries({
    queries: fotos.map((f) => ({
      queryKey: ['taller-pitstop-foto', token, f.id],
      queryFn: () => tallerRpc<{ mime: string; base64: string }>('portal_foto', { _token: token, _foto: f.id }),
      staleTime: Infinity,
    })),
  });
  const porId = useMemo(() => {
    const m = new Map<string, string>();
    fotos.forEach((f, i) => {
      const d = resultados[i]?.data;
      if (d) m.set(f.id, `data:${d.mime};base64,${d.base64}`);
    });
    return m;
  }, [fotos, resultados]);

  if (fotos.length === 0) return null;
  const grupos = new Map<string, PortalFoto[]>();
  fotos.forEach((f) => grupos.set(f.etapa, [...(grupos.get(f.etapa) ?? []), f]));
  const actual = ampliada ? fotos.find((f) => f.id === ampliada) : null;

  return (
    <Seccion icono={<Camera className="h-4 w-4" />} titulo="Fotos del proceso">
      <div className="space-y-3">
        {[...grupos.entries()].map(([etapa, fs]) => (
          <div key={etapa}>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-cyan-300">{etapa.replace(/_/g, ' ')}</p>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {fs.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setAmpliada(f.id === ampliada ? null : f.id)}
                  className={cn('aspect-square overflow-hidden rounded-md border bg-white/5', ampliada === f.id ? 'border-fuchsia-400 ring-2 ring-fuchsia-400/60' : 'border-white/10')}
                >
                  {porId.get(f.id)
                    ? <img src={porId.get(f.id)} alt={f.descripcion ?? etapa} className="h-full w-full object-cover" />
                    : <div className="flex h-full w-full items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-white/40" /></div>}
                </button>
              ))}
            </div>
          </div>
        ))}
        {actual && (
          <div className="rounded-lg border border-fuchsia-400/40 bg-black/30 p-2">
            <img src={porId.get(actual.id)} alt={actual.descripcion ?? ''} className="mx-auto max-h-80 rounded-md object-contain" />
            {actual.descripcion && <p className="mt-2 text-center text-xs text-white/70">{actual.descripcion}</p>}
            <p className="text-center text-[10px] text-white/40">{fmtFechaHora(actual.fecha)}</p>
          </div>
        )}
      </div>
    </Seccion>
  );
}

function Seccion({ icono, titulo, children, acento }: { icono: React.ReactNode; titulo: string; children: React.ReactNode; acento?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
      <h2 className={cn('mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide', acento ?? 'text-cyan-300')}>{icono} {titulo}</h2>
      {children}
    </div>
  );
}

export function PitStop() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [nombre, setNombre] = useState('');
  const [terminosAbiertos, setTerminosAbiertos] = useState(false);
  const [respuestaLocal, setRespuestaLocal] = useState<{ total: number; fecha: Date } | null>(null);
  const [traspaso, setTraspaso] = useState<{ codigo: string; expira: string } | null>(null);

  const { data, isLoading, isError, error } = useQuery<PortalOrden>({
    queryKey: ['taller-pitstop', token],
    queryFn: () => tallerRpc<PortalOrden>('portal_orden', { _token: token }),
    refetchInterval: 30000,
    staleTime: 0,
    retry: 1,
    enabled: !!token,
  });

  // Al llegar un presupuesto nuevo, todas las líneas quedan marcadas por defecto
  // (solo cuando cambia la versión: no se pisa la selección en cada refetch de 30 s).
  useEffect(() => {
    if (data?.presupuesto) setSeleccion(new Set(data.presupuesto.lineas.map((l) => l.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.presupuesto?.version]);

  const responder = useMutation({
    mutationFn: (ids: string[]) => tallerRpc<RespuestaPresupuesto>('portal_aprobar', { _token: token, _aprobadas: ids, _nombre: nombre }),
    onSuccess: (r) => {
      toast.success(r.aprobadas > 0 ? 'Gracias: su aprobación quedó registrada' : 'Quedó registrado: no aprobó el presupuesto');
      setRespuestaLocal({ total: r.total.total, fecha: new Date() });
      void qc.invalidateQueries({ queryKey: ['taller-pitstop', token] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pagar = useMutation({
    mutationFn: (medio: 'tarjeta' | 'deuna') => tallerRpc<RespuestaPago>('portal_pagar', { _token: token, _medio: medio }),
    onSuccess: () => { toast.success('Enlace de pago generado'); void qc.invalidateQueries({ queryKey: ['taller-pitstop', token] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const generarTraspaso = useMutation({
    mutationFn: () => tallerRpc<RespuestaTraspaso>('portal_traspaso_generar', { _token: token }),
    onSuccess: (r) => { setTraspaso({ codigo: r.codigo, expira: r.expira }); toast.success('Código de traspaso generado'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const descargarCertificado = useMutation({
    mutationFn: () => tallerRpc<RespuestaBenchPdf>('portal_bench_pdf', { _token: token }),
    onSuccess: (r) => {
      if (!r.pdf) { toast.error('El PDF aún se está generando: intenta de nuevo en unos minutos'); return; }
      descargarPdfBase64(r.pdf, `certificado-${r.codigo}.pdf`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 text-white">
        <Loader2 className="mr-2 h-6 w-6 animate-spin text-fuchsia-400" /> Cargando tu Pit Stop…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 p-6 text-center text-white">
        <AlertTriangle className="h-10 w-10 text-amber-400" />
        <p className="text-xl font-bold">Enlace no disponible</p>
        <p className="max-w-sm text-sm text-white/60">{getErrorMessage(error) || 'El enlace venció o ya no es válido. Pide al taller que te envíe uno nuevo.'}</p>
      </div>
    );
  }

  const o = data;
  const puedeResponder = o.presupuesto != null && o.estado === 'presupuestado';
  const nombreValido = nombre.trim().length >= 3;
  const totalSeleccionado = (o.presupuesto?.lineas ?? []).filter((l) => seleccion.has(l.id)).reduce((a, l) => a + l.total, 0);

  const toggleLinea = (id: string) => setSeleccion((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleAprobar = () => {
    if (!nombreValido) { toast.error('Escriba su nombre para registrar la aprobación'); return; }
    responder.mutate([...seleccion]);
  };
  const handleNoAprobar = async () => {
    if (!nombreValido) { toast.error('Escriba su nombre para registrar la respuesta'); return; }
    const ok = await dlg.confirmar('¿Confirma que NO aprueba ningún trabajo de este presupuesto?', {
      detalle: 'Quedará registrado con su nombre y la fecha de hoy. El taller se comunicará con usted.', peligro: true, aceptar: 'Sí, no aprobar',
    });
    if (!ok) return;
    responder.mutate([]);
  };
  const handleGenerarTraspaso = async () => {
    const ok = await dlg.confirmar('¿Generar un código de traspaso?', {
      detalle: 'Sirve para que la persona que compre el equipo lo reclame como nuevo dueño desde su pasaporte. Dura 7 días.', aceptar: 'Sí, generar código',
    });
    if (!ok) return;
    generarTraspaso.mutate();
  };

  const listo = o.estado === 'listo' || o.estado === 'entregado';
  const cobro = o.cobro_pendiente;
  const mensajeWsp = traspaso
    ? `Te vendo mi ${[o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ') || o.equipo.tipo}. Revisa su historial aquí: ${urlPasaporte(o.pasaporte ?? '')} y usa el código de traspaso: ${traspaso.codigo}`
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950 pb-10 text-white">
      {dlg.ui && <div className="mx-auto max-w-2xl px-4 pt-4"><div className="text-slate-900">{dlg.ui}</div></div>}

      {/* Encabezado */}
      <header className="border-b border-white/10 bg-black/20 px-4 py-5">
        <div className="mx-auto max-w-2xl">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-300"><Zap className="h-3.5 w-3.5" /> Pit Stop</p>
          <h1 className="mt-0.5 text-lg font-extrabold">{o.taller?.nombre ?? 'Taller'}</h1>
          <p className="mt-2 text-2xl font-black">¡Hola, {o.cliente}! 👋</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1"><Gamepad2 className="h-3.5 w-3.5 text-cyan-300" /> {[o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ') || o.equipo.tipo}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 font-mono">{o.codigo}</span>
            {o.tecnico && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1"><Wrench className="h-3.5 w-3.5" /> Técnico {o.tecnico}</span>}
            {o.fecha_prometida && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1"><CalendarClock className="h-3.5 w-3.5" /> Prometido {fmtFecha(o.fecha_prometida)}</span>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 pt-5">
        {(o.estado === 'rechazado' || o.estado === 'devuelto') && (
          <div className={cn('rounded-xl border px-4 py-3 text-sm font-medium', o.estado === 'rechazado' ? 'border-rose-400/40 bg-rose-500/10 text-rose-200' : 'border-white/20 bg-white/5 text-white/70')}>
            {o.estado === 'rechazado' ? 'El presupuesto no fue aprobado. El taller te contactará.' : 'El equipo fue devuelto sin reparar (diagnóstico cobrado).'}
          </div>
        )}

        {o.garantia_cubre && (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-200">
            <ShieldCheck className="h-6 w-6 shrink-0" /> Reparación cubierta por la garantía: sin costo
          </div>
        )}

        {/* Progreso */}
        <Seccion icono={<Sparkles className="h-4 w-4" />} titulo="Nivel de tu reparación">
          <BarraNiveles estado={o.estado} />
          <p className="mt-3 text-center text-sm font-semibold text-white">{ESTADOS[o.estado].label}</p>
          <p className="text-center text-xs text-white/50">Falla reportada: {o.falla}</p>
        </Seccion>

        {/* Pasaporte del equipo */}
        {o.pasaporte && (
          <Seccion icono={<QrCode className="h-4 w-4" />} titulo="Pasaporte de tu equipo">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
              <div>
                <p className="text-sm text-white/70">Historial de reparaciones, garantía y repuestos de tu equipo, por su número de serie.</p>
                <a href={urlPasaporte(o.pasaporte)} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 underline">
                  Ver el pasaporte de mi equipo <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
              <div className="shrink-0 rounded-md bg-white p-1.5"><QRCodeSVG value={urlPasaporte(o.pasaporte)} size={72} level="M" /></div>
            </div>
          </Seccion>
        )}

        {/* Línea de tiempo */}
        {o.eventos.length > 0 && (
          <Seccion icono={<CalendarClock className="h-4 w-4" />} titulo="Línea de tiempo">
            <ol className="space-y-3">
              {[...o.eventos].reverse().map((ev, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className={cn('mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full', ev.estado && ev.estado in ESTADOS ? ESTADOS[ev.estado as EstadoOrden].cls.split(' ')[0] : 'bg-white/30')} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white/90">{ev.nota ?? (ev.estado ? ESTADOS[ev.estado as EstadoOrden].label : '')}</p>
                    <p className="text-[11px] text-white/40">{fmtFechaHora(ev.fecha)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Seccion>
        )}

        {/* Fotos */}
        <GaleriaFotos token={token} fotos={o.fotos} />

        {/* Presupuesto pendiente */}
        {puedeResponder && o.presupuesto && (
          <Seccion icono={<BadgeCheck className="h-4 w-4" />} titulo={o.presupuesto.adicional ? 'Presupuesto adicional' : 'Presupuesto'} acento="text-amber-300">
            <div className="space-y-2">
              {o.presupuesto.lineas.map((l) => (
                <label key={l.id} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
                  <input type="checkbox" checked={seleccion.has(l.id)} onChange={() => toggleLinea(l.id)} className="mt-0.5 h-4 w-4 accent-fuchsia-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">{l.descripcion}</p>
                    <p className="text-[11px] text-white/50">
                      {l.cantidad} × {money(l.precio)} {l.garantia_dias ? `· garantía ${l.garantia_dias} días` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 font-mono text-sm font-semibold">{money(l.total)}</span>
                </label>
              ))}
              <div className="flex items-center justify-between border-t border-white/10 pt-2 text-sm font-bold">
                <span>Total seleccionado</span><span className="font-mono text-base text-emerald-300">{money(totalSeleccionado)}</span>
              </div>
              <label className="block pt-1">
                <span className="mb-1 block text-xs font-medium text-white/70">Su nombre (para registrar la aprobación)</span>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre y apellido"
                  className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400 focus:outline-none" />
              </label>
              <div className="flex flex-col gap-2 pt-1 sm:flex-row">
                <button onClick={handleAprobar} disabled={responder.isPending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-violet-600 py-2.5 text-sm font-bold shadow-lg disabled:opacity-50">
                  {responder.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Aprobar presupuesto
                </button>
                <button onClick={handleNoAprobar} disabled={responder.isPending}
                  className="rounded-lg border border-white/20 px-4 py-2.5 text-sm font-medium text-white/70 hover:bg-white/5 disabled:opacity-50">
                  No aprobar
                </button>
              </div>
            </div>
          </Seccion>
        )}

        {respuestaLocal && !puedeResponder && (
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            Respuesta registrada el {fmtFechaHora(respuestaLocal.fecha.toISOString())}.
          </div>
        )}

        {/* Aprobado y anticipo */}
        {o.aprobado.total > 0 && (
          <Seccion icono={<ShieldCheck className="h-4 w-4" />} titulo="Trabajo aprobado">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Total aprobado (con IVA)</span>
              <span className="font-mono text-lg font-bold text-emerald-300">{money(o.aprobado.total)}</span>
            </div>
            {o.anticipo_pagado > 0 && (
              <p className="mt-1 text-sm text-white/60">Anticipo pagado: <span className="font-mono text-white">{money(o.anticipo_pagado)}</span></p>
            )}

            {cobro ? (
              <div className="mt-3 rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3 text-center">
                <p className="text-sm text-cyan-200">Cobro de {money(cobro.monto)} en curso ({cobro.medio === 'deuna' ? 'De Una' : 'tarjeta'})</p>
                {cobro.url && (
                  <a href={cobro.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 underline">
                    Abrir enlace de pago <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {(cobro.qr || cobro.url) && (
                  <div className="mt-3 flex justify-center rounded-md bg-white p-2"><QRCodeSVG value={cobro.qr || cobro.url || ''} size={160} level="M" /></div>
                )}
                <p className="mt-2 text-[11px] text-white/40">Esta página se actualiza sola cuando el pago se confirme.</p>
              </div>
            ) : o.anticipo_sugerido != null && o.anticipo_sugerido > 0 ? (
              o.pago_en_linea ? (
                <div className="mt-3 space-y-2">
                  <p className="text-sm text-white/70">Anticipo sugerido ({money(o.anticipo_sugerido)}) para iniciar la reparación:</p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button onClick={() => pagar.mutate('tarjeta')} disabled={pagar.isPending}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 py-2.5 text-sm font-bold shadow-lg disabled:opacity-50">
                      {pagar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pagar anticipo con tarjeta
                    </button>
                    <button onClick={() => pagar.mutate('deuna')} disabled={pagar.isPending}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-white/20 py-2.5 text-sm font-bold text-white/80 hover:bg-white/5 disabled:opacity-50">
                      <Banknote className="h-4 w-4" /> Pagar con De Una
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-white/60">Anticipo sugerido {money(o.anticipo_sugerido)}: puede pagarlo en el local.</p>
              )
            ) : null}
          </Seccion>
        )}

        {/* Listo / entregado */}
        {listo && (
          <Seccion icono={<CheckCircle2 className="h-4 w-4" />} titulo={o.estado === 'listo' ? '¡Tu equipo está listo!' : 'Equipo entregado'} acento="text-emerald-300">
            {o.trabajo_realizado && <p className="text-sm text-white/80">{o.trabajo_realizado}</p>}
            {o.garantia_hasta && <p className="mt-1 text-xs text-white/50">Garantía hasta {fmtFecha(o.garantia_hasta)}</p>}
          </Seccion>
        )}

        {/* Certificado de bench */}
        {o.certificado && (
          <Seccion icono={<ThermometerSun className="h-4 w-4" />} titulo="Tu certificado de bench" acento="text-emerald-300">
            <p className="text-sm text-white/70">
              {o.certificado.firmado ? 'Informe de pruebas firmado electrónicamente.' : 'Informe de pruebas realizadas a tu equipo.'} Verificable por cualquiera con el código.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button onClick={() => descargarCertificado.mutate()} disabled={descargarCertificado.isPending || !o.certificado.pdf}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 py-2.5 text-sm font-bold shadow-lg disabled:opacity-50">
                {descargarCertificado.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {o.certificado.pdf ? 'Descargar PDF' : 'Generando PDF…'}
              </button>
              <a href={urlVerificarBench(o.certificado.codigo)} target="_blank" rel="noopener noreferrer"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/5">
                Verificar autenticidad <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </Seccion>
        )}

        {/* ¿Vas a vender tu equipo? */}
        {o.puede_traspasar && (
          <Seccion icono={<KeyRound className="h-4 w-4" />} titulo="¿Vas a vender tu equipo?" acento="text-amber-300">
            {!traspaso ? (
              <div className="space-y-2">
                <p className="text-sm text-white/70">Genera un código para que la persona que compre tu equipo pueda reclamarlo como nuevo dueño desde su pasaporte. La garantía vigente viaja con el equipo.</p>
                <button onClick={handleGenerarTraspaso} disabled={generarTraspaso.isPending}
                  className="flex items-center justify-center gap-2 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-bold text-white/85 hover:bg-white/5 disabled:opacity-50">
                  {generarTraspaso.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Generar código de traspaso
                </button>
              </div>
            ) : (
              <div className="space-y-2 text-center">
                <p className="font-mono text-3xl font-black tracking-widest text-amber-300">{traspaso.codigo}</p>
                <p className="text-xs text-white/50">Válido hasta {fmtFechaHora(traspaso.expira)} (7 días) · 5 intentos</p>
                <a href={`https://wa.me/?text=${encodeURIComponent(mensajeWsp)}`} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 text-sm font-bold shadow-lg">
                  <Share2 className="h-4 w-4" /> Compartir por WhatsApp
                </a>
              </div>
            )}
          </Seccion>
        )}

        {/* Términos */}
        {o.taller?.terminos && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02]">
            <button onClick={() => setTerminosAbiertos((v) => !v)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-white/60">
              {terminosAbiertos ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Términos y condiciones del taller
            </button>
            {terminosAbiertos && <p className="whitespace-pre-line px-4 pb-4 text-xs text-white/50">{o.taller.terminos}</p>}
          </div>
        )}

        <p className="pt-2 text-center text-[11px] text-white/30">Orden {o.codigo} · recibida el {fmtFecha(o.fecha)}</p>
      </main>
    </div>
  );
}
